import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useRequireRole } from "@/lib/use-require-role";
import { PageHeader } from "@/components/page-header";
import { Input } from "@/components/ui/input";
import { organizations, fmtCents } from "@/mocks/saas-data";

export const Route = createFileRoute("/admin/organizations")({
  ssr: false,
  head: () => ({ meta: [{ title: "Admin — Organizations" }] }),
  component: Page,
});

function Page() {
  const user = useRequireRole(["superadmin"]);
  const [q, setQ] = useState("");
  const filtered = useMemo(
    () => organizations.filter((o) => o.name.toLowerCase().includes(q.toLowerCase()) || o.slug.includes(q.toLowerCase())),
    [q],
  );
  if (!user) return null;

  return (
    <div className="mx-auto max-w-7xl px-6 py-8 lg:px-10 lg:py-10">
      <PageHeader title="Organizations" description="All tenants on the platform." />
      <div className="mb-4">
        <Input placeholder="Search by name or slug…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" />
      </div>
      <div className="overflow-hidden rounded-xl bg-card ring-1 ring-border">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-border bg-surface-muted/50">
            <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3 font-medium">Organization</th>
              <th className="px-4 py-3 font-medium">Plan</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Seats</th>
              <th className="px-4 py-3 font-medium">Storage</th>
              <th className="px-4 py-3 font-medium">MRR</th>
              <th className="px-4 py-3 font-medium">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map((o) => (
              <tr key={o.id} className="hover:bg-surface-muted/30">
                <td className="px-4 py-3">
                  <Link to="/admin/organizations/$id" params={{ id: o.id }} className="flex items-center gap-2 font-medium hover:underline">
                    <span
                      className="flex size-6 items-center justify-center rounded text-[10px] font-semibold text-background"
                      style={{ background: `oklch(0.45 0.12 ${o.brandHue})` }}
                    >
                      {o.logoLetter}
                    </span>
                    {o.name}
                  </Link>
                </td>
                <td className="px-4 py-3 capitalize text-muted-foreground">{o.planId}</td>
                <td className="px-4 py-3">
                  <span className={`rounded px-2 py-0.5 font-mono text-[10px] uppercase ${o.status === "active" ? "bg-success-soft text-success" : o.status === "trialing" ? "bg-amber-500/15 text-amber-700 dark:text-amber-300" : "bg-destructive/15 text-destructive"}`}>
                    {o.status}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono">{o.seats}</td>
                <td className="px-4 py-3 font-mono text-muted-foreground">{o.storageUsedGb} GB</td>
                <td className="px-4 py-3 font-mono">{fmtCents(o.mrrCents)}</td>
                <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">{o.createdAt}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">No organizations match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
